<template>
	<v-dialog v-model="showAddDataDialog" width="600px" persistent>
		<file-selection-dialog :allowedFileTypes="allowedFileTypes" :source="uploadSource" :preselectedFile="selectedFile"
			@close="closeDialog" @confirm="openTerrainDataFromFile" @confirmTiffPair="openDtmOrthoTypeSelection" />
	</v-dialog>
	<!-- tiff の組が揃ったときだけマウントする。facade は子が持つ（GI-17） -->
	<dtm-ortho-type-flow v-if="dtmOrthoPair" :pair="dtmOrthoPair" @previous="backToFileSelectionFromDtmOrtho"
		@cancel="closeDtmOrtho" @finished="closeDtmOrtho" />
	<v-dialog v-model="showTerrainDataDialog" width="600px" persistent>
		<add-terrain-data ref="add_terrain_data" @upload="uploadTerrainData" @previous="backToFileSelection"
			@close="closeTerrainData" :allowedFileTypes="allowedFileTypes" :axis_show="axisShow"
			:is_show="showTerrainDataDialog" :is_drawing_2d_image="isDrawing2dImage" :default_file_name="selectedFileName"
			:selected_file="selectedFile" :resolvedType="resolvedType" />
	</v-dialog>
	<v-dialog v-model="showDrawing2DImageConstructionDialog" width="600px" persistent>
		<save-reference-topology-dialog ref="add_terrain_data" @save="saveReferenceTopology" @close="closeTerrainData" />
	</v-dialog>
	<add-data-from-groupware-sequence v-if="showGetFromGroupwareDialog" @close="closeGetFromGroupwareDialog" />
</template>

<script>
import { useUploadTracker } from "@/composables/useUploadTracker";
import {
	handleCustomError,
	handleSingleUploadError,
	handleSingleUploadSuccess,
	notifyError,
	notifyWarning,
} from "@/utils/notificationUtils";
import axios from "axios";
import { Cartographic, Math as CesiumMath } from "cesium";
import { mapActions, mapMutations } from "vuex";
import {
	pickPositions,
	toggleDrawing2dImageVisibility,
} from "../../utils/cesium-common";
import {
	getDesign3dBucketInfo,
	importDesign,
} from "../../utils/designApiUtils";
import { uploadLandlogS3 } from "../../utils/fileStorageUtils";
import { importPointCloud } from "../../utils/pointCloudApiUtils";
import DtmOrthoTypeFlow from "../AddData/DtmOrthoTypeFlow.vue";
import FileSelectionDialog from "../AddData/FileSelectionDialog.vue";
import addDataFromGroupwareSequence from "../AddData/addDataFromGroupwareSequence.vue";
import addTerrainData from "../AddData/addTerrainData.vue";
import saveReferenceTopologyDialog from "../AddData/saveReferenceTopologyDialog.vue";
import {
	getAllowedFileTypesString,
	uploadSources,
} from "../AddData/uploadTypeResolver";

const DEFAULT_IMAGE_WIDTH = 100;
const DEFAULT_LOCAL_ALLOWED_FILE_TYPES = getAllowedFileTypesString(
	uploadSources.local,
);

export default {
	components: {
		FileSelectionDialog,
		DtmOrthoTypeFlow,
		addTerrainData,
		saveReferenceTopologyDialog,
		addDataFromGroupwareSequence,
	},
	props: {
		modelValue: Boolean,
		mode: {
			type: String,
			default: "local",
		},
	},
	data() {
		return {
			type: "",
			resolvedType: "",
			selectedFile: null,
			/** 種別選択中の tiff の組（origin: "local"）。null のときフローはマウントしない */
			dtmOrthoPair: null,
			showTerrainDataDialog: false,
			showDrawing2DImageConstructionDialog: false,
			showGetFromGroupwareDialog: false,
			axisShow: false,
			allowedFileTypes: DEFAULT_LOCAL_ALLOWED_FILE_TYPES,
			isDrawing2dImage: true,
			origin_latitude: 0,
			origin_longitude: 0,
			drawing2DImageConstructionData: undefined,
		};
	},
	mounted() {
		window.addEventListener("createDrawing2dImage", this.createDrawing2dImage);
	},
	computed: {
		showAddDataDialog: {
			get() {
				return this.modelValue;
			},
			set(value) {
				this.$emit("update:modelValue", value);
			},
		},
		selectedFileName() {
			return this.selectedFile?.name ?? "";
		},
		uploadSource() {
			return this.mode === "groupware"
				? uploadSources.groupware
				: uploadSources.local;
		},
	},
	watch: {
		modelValue(value) {
			if (!value) {
				return;
			}
			if (this.mode === "groupware") {
				this.showAddDataDialog = false;
				this.showGetFromGroupwareDialog = true;
			}
		},
	},
	methods: {
		...mapMutations(["set_snackbar"]),
		...mapActions(["get_point_cloud_list", "get_design_list"]),
		...mapActions("drawing2dImage", [
			"getDrawing2dImageList",
			"addDrawing2dImage",
		]),
		closeDialog() {
			this.selectedFile = null;
			this.resolvedType = "";
			this.$emit("close");
		},
		async openTerrainDataFromFile(file, resolvedType) {
			this.showAddDataDialog = false;

			if (resolvedType === "drawing2dImage") {
				notifyWarning(this, this.$t("CLICK_ON_THE_MAP_TO_SET_IMAGE"));
				const positionCartesianList = await pickPositions(1);
				const positionCartographic = Cartographic.fromCartesian(
					positionCartesianList[0],
				);

				this.origin_latitude = CesiumMath.toDegrees(
					Number(positionCartographic.latitude),
				);
				this.origin_longitude = CesiumMath.toDegrees(
					Number(positionCartographic.longitude),
				);
			}

			this.selectedFile = file;
			this.type = resolvedType;
			this.resolvedType = resolvedType;
			this.axisShow = resolvedType === "pointcloud";
			this.isDrawing2dImage = resolvedType === "drawing2dImage";
			this.$store.commit("reset_snackbars");
			this.showTerrainDataDialog = true;
		},
		createDrawing2dImage(event) {
			if (event.detail) {
				console.log("createDrawing2dImage", event.detail);
				this.type = "drawing2dImage";
				this.resolvedType = "";
				this.selectedFile = null;
				this.allowedFileTypes = "";
				this.axisShow = false;
				this.isDrawing2dImage = true;
				this.showDrawing2DImageConstructionDialog = true;
				this.drawing2DImageConstructionData = event.detail;
			} else {
				this.showDrawing2DImageConstructionDialog = false;
				notifyError(this, this.$t("FAILED_TO_CREATE_REFERENCE_TOPOLOGY"));
			}
		},
		closeGetFromGroupwareDialog() {
			this.showGetFromGroupwareDialog = false;
			this.$emit("close");
		},
		closeTerrainData() {
			this.type = "";
			this.resolvedType = "";
			this.selectedFile = null;
			this.allowedFileTypes = DEFAULT_LOCAL_ALLOWED_FILE_TYPES;
			this.axisShow = false;
			this.showTerrainDataDialog = false;
			this.showDrawing2DImageConstructionDialog = false;
			this.isDrawing2dImage = false;
			this.imageWidthAndHeight = { width: 50, height: 50 };
		},
		backToFileSelection() {
			this.showTerrainDataDialog = false;
			this.showAddDataDialog = true;
		},
		// tiff ペアを受けて種別選択ダイアログに進む（AC-5）。File 2 件は手動経路の組（GI-28）
		openDtmOrthoTypeSelection(files) {
			this.showAddDataDialog = false;
			this.$store.commit("reset_snackbars");
			this.dtmOrthoPair = { origin: "local", files };
		},
		backToFileSelectionFromDtmOrtho() {
			this.dtmOrthoPair = null;
			this.showAddDataDialog = true;
		},
		/** 「キャンセル」と取込の完了 / 失敗はどちらもフロー全体を閉じる（AC-9） */
		closeDtmOrtho() {
			this.dtmOrthoPair = null;
			this.selectedFile = null;
			this.$emit("close");
		},
		async uploadTerrainData(dialogData) {
			if (!dialogData) {
				this.closeTerrainData();
				return;
			}

			// Prepare common data before closing dialog
			const commonData = await this.prepareCommonData(dialogData);
			const selectedType = dialogData[dialogData.length - 1] || this.type;

			this.closeTerrainData();

			await this.$store.dispatch("executeWithSpinner", async () => {
				try {
					// Handle upload based on type
					switch (selectedType) {
						case "pointcloud":
							await this.uploadPointCloud(dialogData, commonData);
							break;
						case "design":
							await this.uploadDesign(dialogData, commonData);
							break;
						case "lineworks":
							await this.uploadLineworks(dialogData, commonData);
							break;
						case "linearfile":
							await this.uploadLinearFile(dialogData, commonData);
							break;
						case "drawing2dImage":
							await this.uploadDrawing2dImage(dialogData, commonData);
							break;
						default:
							throw new Error(`Unknown upload type: ${selectedType}`);
					}
				} catch (error) {
					handleSingleUploadError(this, error, dialogData[1]);
				}
			});
		},

		async prepareCommonData(dialogData) {
			const body = {
				name: dialogData[1],
				date: dialogData[2],
				time: dialogData[3] + ":00",
			};

			if (this.axisShow) {
				body.axis = dialogData[4];
				body.distance_unit = dialogData[5];
			}

			const extension = dialogData[0].name.substring(
				dialogData[0].name.lastIndexOf(".") + 1,
			);

			return { body, extension };
		},

		async uploadPointCloud(dialogData, { body, extension }) {
			const siteId = this.$route.query.siteId;
			const fileData = dialogData[0];

			try {
				// Step 1: Upload file to Landlog S3
				const bucketInfo = await getDesign3dBucketInfo(siteId, "pointcloud");
				const bucketId = bucketInfo.data.bucketId;
				const parentNodeId = bucketInfo.data.parentNodeId;
				const dateName = dialogData[2].replace(/-/g, "");

				const node_id = await uploadLandlogS3(
					bucketId,
					parentNodeId,
					`${dialogData[1]}_${dateName}.${extension}`,
					fileData,
				);
				body.bucket_id = bucketId;
				body.node_id = node_id.data.node.id;

				// Step 2: Register + start processing on v2-server (async, returns 202)
				const { point_cloud_id } = await importPointCloud(siteId, {
					name: body.name,
					date: body.date,
					time: body.time,
					file_name: fileData.name,
					axis: body.axis,
					distance_unit: body.distance_unit,
					bucket_id: body.bucket_id,
					node_id: body.node_id,
				});
				useUploadTracker().trackItem(
					{ dataType: "pointcloud", id: point_cloud_id },
					siteId,
				);

				// Step 3: Update UI
				await this.get_point_cloud_list(siteId);

				// Show success message
				handleSingleUploadSuccess(this, dialogData[1]);
			} catch (error) {
				console.error("Point cloud upload failed:", error);
				throw error;
			}
		},

		async uploadDesign(dialogData, { body, extension }) {
			const fileData = dialogData[0];
			const siteId = this.$route.query.siteId;

			try {
				// Step 1: Upload file to Landlog S3
				const bucketInfo = await getDesign3dBucketInfo(siteId, "design");
				const bucketId = bucketInfo.data.bucketId;
				const parentNodeId = bucketInfo.data.parentNodeId;
				const dateName = dialogData[2].replace(/-/g, "");

				const node_id = await uploadLandlogS3(
					bucketId,
					parentNodeId,
					`${dialogData[1]}_${dateName}.${extension}`,
					fileData,
				);

				body.bucket_id = bucketId;
				body.node_id = node_id.data.node.id;

				// Step 2: Register + start processing on v2-server (async, returns 202)
				const { design_id } = await importDesign(siteId, {
					name: body.name,
					date: body.date,
					time: body.time,
					bucket_id: body.bucket_id,
					node_id: body.node_id,
				});
				useUploadTracker().trackItem(
					{ dataType: "design", id: design_id },
					siteId,
				);

				// Step 3: Update UI
				await this.get_design_list(siteId);

				// Show success message
				handleSingleUploadSuccess(this, dialogData[1]);
			} catch (error) {
				console.error("Design upload failed:", error);
				throw error;
			}
		},
		async uploadLineworks(dialogData, { body, extension }) {
			const fileData = dialogData[0];
			const siteId = this.$route.query.siteId;

			// Get bucket info and upload to S3
			const bucketInfo = await getDesign3dBucketInfo(siteId, "lineworks");
			const bucketId = bucketInfo.data.bucketId;
			const parentNodeId = bucketInfo.data.parentNodeId;
			const dateName = dialogData[2].replace(/-/g, "");

			const node_id = await uploadLandlogS3(
				bucketId,
				parentNodeId,
				`${dialogData[1]}_${dateName}.${extension}`,
				fileData,
			);

			body.bucket_id = bucketId;
			body.node_id = node_id.data.node.id;
			// Fallback unit for DXF without $INSUNITS (SIMA is fixed to meters)
			body.site_distance_unit = this.$store.state.site?.units?.distance;

			// Call lineworks API
			const res = await axios.post(
				`${import.meta.env.VITE_API_BASE}/assetLink/lineworks/${siteId}`,
				body,
				{ auth: this.$store.state.authObject },
			);

			const line = {
				data_type: "lineworks",
				line_type: "dxf",
				lineworks_id: res.data.lineworks_id,
				create_at: res.data.create_at,
				lineworks_name: res.data.lineworks_name,
				lineworks_path: res.data.lineworks_path,
				processing_status: res.data.processing_status,
				site_id: res.data.site_id,
			};
			this.$store.commit("add_design", line);
			useUploadTracker().trackItem(
				{ dataType: "lineworks", id: res.data.lineworks_id },
				siteId,
			);
			// Show success message for DXF upload
			handleSingleUploadSuccess(this, dialogData[1]);
		},

		async uploadLinearFile(dialogData, { body, extension }) {
			const fileData = dialogData[0];
			const siteId = this.$route.query.siteId;
			let viewerLineId;

			try {
				if (!window["viewer"]?.loadSIMAFile) {
					throw new Error(
						"Viewer is not initialized or loadSIMAFile is not available",
					);
				}
				const buffer =
					typeof fileData.arrayBuffer === "function"
						? await fileData.arrayBuffer()
						: fileData;
				viewerLineId = await window["viewer"].loadSIMAFile(buffer);
				if (!viewerLineId) {
					throw new Error("Failed to render SIMA file in viewer");
				}
			} catch (err) {
				handleCustomError(
					this,
					err,
					this.$t("INVALID_SIMA_UPLOAD_FAILED", { name: dialogData[1] }),
				);
				return;
			}

			try {
				// Get bucket info and upload to S3
				const bucketInfo = await getDesign3dBucketInfo(siteId, "linearfile");
				const bucketId = bucketInfo.data.bucketId;
				const parentNodeId = bucketInfo.data.parentNodeId;
				const dateName = dialogData[2].replace(/-/g, "");

				const node_id = await uploadLandlogS3(
					bucketId,
					parentNodeId,
					`${dialogData[1]}_${dateName}.${extension}`,
					fileData,
				);

				body.bucket_id = bucketId;
				body.node_id = node_id.data.node.id;

				// Call linearfile API
				const res = await axios.post(
					`${import.meta.env.VITE_API_BASE}/assetLink/linearfile/${siteId}`,
					body,
					{ auth: this.$store.state.authObject },
				);

				const simaData = viewerLineId
					? { ...res.data, viewerLineId }
					: res.data;
				this.$store.commit("simaLine/addSimaLine", simaData);
				handleSingleUploadSuccess(this, dialogData[1], {
					customMessage: `${dialogData[1]} ${this.$t("UPLOAD")} ${this.$t("successful")}`,
				});
			} catch (e) {
				if (viewerLineId && window["viewer"]?.removeSIMA) {
					window["viewer"].removeSIMA(viewerLineId);
				}
				const errorStatus =
					typeof e.toJSON === "function" ? e.toJSON().status : undefined;
				if (errorStatus === 422) {
					handleCustomError(
						this,
						e,
						this.$t("INVALID_SIMA_UPLOAD_FAILED", { name: dialogData[1] }),
					);
					return;
				}
				throw e;
			}
		},

		async uploadDrawing2dImage(dialogData, { body, extension }) {
			// Add drawing2dImage specific data
			body.isDraped = dialogData[4];
			body.centerLatitude = this.origin_latitude;
			body.centerLongitude = this.origin_longitude;

			// Get image dimensions
			const dimensions = await this.getImageDimensions(dialogData[0]);
			this.imageWidthAndHeight = {
				width: DEFAULT_IMAGE_WIDTH,
				height: Math.round(
					(DEFAULT_IMAGE_WIDTH / dimensions.width) * dimensions.height,
				),
			};

			body.imageWidth = this.imageWidthAndHeight.width;
			body.imageHeight = this.imageWidthAndHeight.height;

			// Upload process
			const fileData = dialogData[0];
			const siteId = this.$route.query.siteId;

			const bucketInfo = await getDesign3dBucketInfo(siteId, "drawing2dImage");
			const bucketId = bucketInfo.data.bucketId;
			const parentNodeId = bucketInfo.data.parentNodeId;
			const dateName = dialogData[2].replace(/-/g, "");

			const node_id = await uploadLandlogS3(
				bucketId,
				parentNodeId,
				`${dialogData[1]}_${dateName}.${extension}`,
				fileData,
			);

			body.bucket_id = bucketId;
			body.node_id = node_id.data.node.id;

			// Call drawing2dImage API
			const res = await axios.post(
				`${import.meta.env.VITE_API_BASE}/assetLink/drawing2dImage/${siteId}`,
				body,
				{ auth: this.$store.state.authObject },
			);

			// Update UI
			await this.getDrawing2dImageList(siteId);
			const entity = this.$store.getters[
				"drawing2dImage/getDrawing2dImageList"
			].find((entity) => entity.assetId === res.data.id);

			if (entity && !entity.getIsVisible()) {
				await toggleDrawing2dImageVisibility(entity);
			}
		},

		getImageDimensions(file) {
			return new Promise((resolve, reject) => {
				const img = new Image();
				img.src = URL.createObjectURL(file);

				img.onload = () => {
					const dimensions = {
						width: img.width,
						height: img.height,
					};
					// Prevent memory leaks
					URL.revokeObjectURL(img.src);
					resolve(dimensions);
				};

				img.onerror = (error) => {
					reject(error);
				};
			});
		},

		async saveReferenceTopology(dialogData) {
			if (dialogData) {
				this.$store.dispatch("updateIsLoading", true);
				try {
					const res = await axios.post(
						`${import.meta.env.VITE_API_BASE}/drawing2dImage/noImage/${
							this.$route.query.siteId
						}`,
						{
							name: dialogData[0],
							date: dialogData[1],
							time: dialogData[2] + ":00",
							isDraped: dialogData[3],
							centerLatitude: this.origin_latitude,
							centerLongitude: this.origin_longitude,
							json: {
								version: 1,
								centerLatitude: this.origin_latitude,
								centerLongitude: this.origin_longitude,
								cornerPositions:
									this.drawing2DImageConstructionData.cornerPositions,
								imageWidth: 0,
								imageHeight: 0,
								isDraped: dialogData[3],
								transparency: 0.5,
								centerHeight: this.drawing2DImageConstructionData.elevation,
								modelMatrix: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
							},
						},
						{
							auth: this.$store.state.authObject,
						},
					);
					await this.getDrawing2dImageList(this.$route.query.siteId);
					const entity = this.$store.getters[
						"drawing2dImage/getDrawing2dImageList"
					].find((entity) => entity.assetId === res.data.id);
					if (entity && !entity.getIsVisible()) {
						await toggleDrawing2dImageVisibility(entity);
					}
				} catch (e) {
					handleSingleUploadError(this, e, dialogData[0]);
				} finally {
					this.$store.dispatch("updateIsLoading", false);
					this.closeTerrainData();
				}
			} else {
				this.closeTerrainData();
			}
		},
	},
};
</script>
